import { DisciplineDetail, EventDetails } from '../types';
import { SITE_URL } from './site';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// "Sunday 14 March 2027". Event dates are stored as midnight UTC, so use UTC
// to keep the day right; built by hand because locale formatting varies by browser.
const formatEventDate = (date: Date) =>
  `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;

const isoDate = (date: Date) => date.toISOString().slice(0, 10);

// "Mooloolaba Triathlon – 14 Mar 2027": the date helps searches like "... 2027".
export const eventPageTitle = (event: EventDetails) =>
  `${event.Name} – ${event.EventDate.getUTCDate()} ${MONTHS[event.EventDate.getUTCMonth()].slice(0, 3)} ${event.EventDate.getUTCFullYear()}`;

// Permanent image address for link previews; the backend redirects to the S3 image.
export const eventImageUrl = (apiUrl: string, eventId: number) => `${new URL(apiUrl).origin}/eventimage/${eventId}`;

const countryFor = (postcode: string) => (/^\d{4}$/.test(postcode.trim()) ? 'AU' : 'GB');

// Only show links that are plain web addresses (the data comes from a spreadsheet).
export const safeWebsite = (url?: string): string | undefined =>
  url && /^https?:\/\//i.test(url.trim()) ? url.trim() : undefined;

// An event is past once its day has ended. Event dates are midnight UTC, so
// compare calendar days rather than instants.
export const isPastEvent = (eventDate: Date, now: Date = new Date()): boolean => {
  const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return eventDate.getTime() < todayUTC;
};

const unit = (metric: string) => (/^met(er|re)s?$/i.test(metric) ? 'm' : metric);

// "Olympic (1500 m swim, 40 km cycle, 10 km run)"
export const formatDistances = (disciplines: DisciplineDetail[]): string[] => {
  const byCategory = new Map<string, string[]>();
  disciplines.forEach((d) => {
    const parts = byCategory.get(d.Category) ?? [];
    parts.push(`${d.TotalDistance} ${unit(d.Metric)} ${d.DisciplineName.toLowerCase()}`);
    byCategory.set(d.Category, parts);
  });
  return Array.from(byCategory, ([category, parts]) => `${category} (${parts.join(', ')})`);
};

export const eventMetaDescription = (event: EventDetails, disciplines: DisciplineDetail[]): string => {
  const place = [event.City, event.County].filter((p) => p && p.trim()).join(', ');
  const sentences = [`${event.Name} on ${formatEventDate(event.EventDate)}${place ? ` in ${place}` : ''}.`];

  const distances = formatDistances(disciplines);
  if (distances.length > 0) {
    sentences.push(`Distances: ${distances.join(', ')}.`);
  }
  if (event.EventOrganiser) {
    sentences.push(`Organised by ${event.EventOrganiser}.`);
  }
  return sentences.join(' ');
};

// schema.org SportsEvent, which can show the event in Google's event listings.
// schema.org SportsEvent for Google's event results. offers (ticket prices)
// and performer (concerts/shows) are left out: we have no prices, and
// participation races have no performer.
export const eventStructuredData = (event: EventDetails, description: string, imageUrl?: string) => ({
  '@context': 'https://schema.org',
  '@type': 'SportsEvent',
  name: event.Name,
  description,
  sport: event.EventType,
  startDate: isoDate(event.EventDate),
  // Only one date is held per event; most are single-day races.
  endDate: isoDate(event.EventDate),
  ...(imageUrl && { image: [imageUrl] }),
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  url: `${SITE_URL}/event/${event.Id}`,
  location: {
    '@type': 'Place',
    name: event.Street || event.City,
    address: {
      '@type': 'PostalAddress',
      streetAddress: event.Street,
      addressLocality: event.City,
      addressRegion: event.County,
      postalCode: event.PostCode,
      addressCountry: countryFor(event.PostCode),
    },
  },
  ...(event.EventOrganiser && {
    organizer: {
      '@type': 'Organization',
      name: event.EventOrganiser,
      ...(safeWebsite(event.Website) && { url: safeWebsite(event.Website) }),
    },
  }),
});
